import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface VectorOpLog {
  id: string;
  posit_id: string | null;
  user_id: string | null;
  operation_type: string;
  status: string;
  error_message?: string | null;
  error_details?: Record<string, unknown> | null;
  pinecone_namespace?: string | null;
  function_name?: string | null;
  retry_count?: number | null;
  created_at: string;
}

export default function ErrorLogs() {
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [status, setStatus] = useState<'failed' | 'pending' | 'retrying' | 'all'>('failed');

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['admin-error-logs', timeRange, status],
    queryFn: async () => {
      const hoursMap = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 } as const;
      const since = new Date(Date.now() - hoursMap[timeRange] * 60 * 60 * 1000).toISOString();

      let query = supabase
        .from('vector_operations_log')
        .select('id, posit_id, user_id, operation_type, status, error_message, error_details, pinecone_namespace, function_name, retry_count, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(500);

      if (status !== 'all') {
        query = query.eq('status', status);
      }

      const { data } = await query;
      return (data || []) as VectorOpLog[];
    }
  });

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Error Logs</h1>
            <p className="text-muted-foreground">Vector operation failures and retries.</p>
          </div>
          <div className="flex gap-2">
            <Select value={timeRange} onValueChange={(v: any) => setTimeRange(v)}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Last 24h</SelectItem>
                <SelectItem value="7d">Last 7d</SelectItem>
                <SelectItem value="30d">Last 30d</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v: any) => setStatus(v)}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="failed">failed</SelectItem>
                <SelectItem value="pending">pending</SelectItem>
                <SelectItem value="retrying">retrying</SelectItem>
                <SelectItem value="all">all</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Operation</TableHead>
                <TableHead>Posit</TableHead>
                <TableHead>Namespace</TableHead>
                <TableHead>Function</TableHead>
                <TableHead>Error</TableHead>
                <TableHead>Retries</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(logs || []).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant={row.status === 'failed' ? 'destructive' : 'secondary'}>{row.status}</Badge>
                  </TableCell>
                  <TableCell>{row.operation_type}</TableCell>
                  <TableCell className="font-mono text-xs">{row.posit_id || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{row.pinecone_namespace || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{row.function_name || '-'}</TableCell>
                  <TableCell className="text-xs max-w-[420px] truncate" title={row.error_message || ''}>{row.error_message || '-'}</TableCell>
                  <TableCell>{row.retry_count ?? 0}</TableCell>
                </TableRow>
              ))}
              {!isLoading && (logs || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">No error logs</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}


